import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import './AffiliateReport.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getToday = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const getMonthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

function AffiliateReport() {
  const [report, setReport] = useState([]);
  const [totals, setTotals] = useState({ affiliate_submissions: 0, direct_submissions: 0, grand_total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [dateFrom, setDateFrom] = useState(getMonthStart());
  const [dateTo, setDateTo] = useState(getToday());
  const [useDateRange, setUseDateRange] = useState(true);

  const fetchReport = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    try {
      let url = `${API_URL}/affiliates/report`;
      if (useDateRange && dateFrom && dateTo) {
        url += `?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReport(data.report || []);
        setTotals(data.totals || { affiliate_submissions: 0, direct_submissions: 0, grand_total: 0 });
      } else {
        toast.error('Failed to load report');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, useDateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async () => {
    const token = localStorage.getItem('admin_token');
    setExporting(true);
    try {
      let url = `${API_URL}/affiliates/report/export`;
      if (useDateRange && dateFrom && dateTo) {
        url += `?dateFrom=${dateFrom}&dateTo=${dateTo}`;
      }
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        const label = useDateRange && dateFrom && dateTo
          ? `${dateFrom}-to-${dateTo}`
          : 'all-time';
        a.download = `affiliate-report-${label}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Export successful');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const maxCount = report.reduce((max, r) => Math.max(max, r.submission_count || 0), 0);

  return (
    <div className="affiliate-report-page">
      {/* Filters */}
      <div className="report-filters">
        <div className="filter-group checkbox-inline">
          <input
            type="checkbox"
            id="use_date_range"
            checked={useDateRange}
            onChange={(e) => setUseDateRange(e.target.checked)}
          />
          <label htmlFor="use_date_range">Filter by date range</label>
        </div>
        <div className="filter-group">
          <label>Date From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            disabled={!useDateRange}
          />
        </div>
        <div className="filter-group">
          <label>Date To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            disabled={!useDateRange}
          />
        </div>
        <button className="btn btn-primary" onClick={fetchReport} disabled={loading}>
          🔍 Refresh
        </button>
        <button className="btn btn-success" onClick={handleExport} disabled={exporting || loading}>
          {exporting ? 'Exporting...' : '📥 Export Excel'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-icon affiliate">🤝</div>
          <div className="summary-content">
            <h3>Affiliate Submissions</h3>
            <p className="summary-value">{totals.affiliate_submissions}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon direct">🌐</div>
          <div className="summary-content">
            <h3>Direct Submissions</h3>
            <p className="summary-value">{totals.direct_submissions}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon total">📊</div>
          <div className="summary-content">
            <h3>Grand Total</h3>
            <p className="summary-value">{totals.grand_total}</p>
          </div>
        </div>
      </div>

      {/* Report Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
        </div>
      ) : report.length === 0 ? (
        <div className="report-card empty-state">
          <span className="empty-icon">📭</span>
          <h3>No affiliates found</h3>
          <p>Create affiliates in the Affiliates page to start tracking.</p>
        </div>
      ) : (
        <div className="report-card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Submissions</th>
                  <th>WhatsApp</th>
                  <th>Telegram</th>
                  <th>Download</th>
                  <th>Official Site</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r, idx) => {
                  const pct = maxCount > 0 ? Math.round((r.submission_count / maxCount) * 100) : 0;
                  return (
                    <tr key={r.id ? `a-${r.id}` : `orphan-${idx}-${r.code}`}>
                      <td className="code-cell">
                        {r.code}
                        {r.orphan && <span className="orphan-tag" title="Submissions exist for this code but the affiliate has been deleted">deleted</span>}
                      </td>
                      <td>{r.description || <span className="muted">—</span>}</td>
                      <td>
                        {r.orphan ? (
                          <span className="status-badge inactive">Deleted</span>
                        ) : (
                          <span className={`status-badge ${r.is_active ? 'active' : 'inactive'}`}>
                            {r.is_active ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="count-cell">
                        <div className="count-wrap">
                          <span className="count-number">{r.submission_count}</span>
                          <div className="count-bar-track">
                            <div className="count-bar-fill" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      </td>
                      <td>{r.whatsapp_count}</td>
                      <td>{r.telegram_count}</td>
                      <td>{r.download_count}</td>
                      <td>{r.official_site_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AffiliateReport;
